import React, { useEffect, useState } from 'react';
import CostingBasisNote from './CostingBasisNote';
import {
  Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { InventoryReportService } from '../../services/inventoryReport.service';
import { InventoryTieOutReport } from '../../interfaces/inventory/inventoryTieOut.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const money = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const firstOfMonth = () => {
  const d = new Date();
  return toLocalIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const KIND_LABEL: Record<string, string> = {
  ISSUE_NOT_POSTED: 'Issue not posted to ledger',
  MANUAL_STOCK_IN: 'Stock-in not posted to ledger',
};

const InventoryLedgerTieOut: React.FC = () => {
  const toast = useToast();
  const [asOf, setAsOf] = useState(toLocalIsoDate());
  const [from, setFrom] = useState(firstOfMonth());
  const [report, setReport] = useState<InventoryTieOutReport | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      setReport(await InventoryReportService.tieOut(institutionId!, asOf, from));
    } catch (e) {
      toast.showApiError(e, 'Failed to load inventory vs ledger report');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleExportCsv = () => {
    if (!report) return;
    const rows: (string | number)[][] = [
      ...report.stores.map(s => [s.storeName, s.stockValuation, '', '']),
      ['Total', report.stockValuation, report.glBalance, report.difference],
    ];
    downloadCsv(`inventory-vs-ledger-${asOf}.csv`, ['Store', 'Stock valuation', 'General ledger', 'Difference'], rows);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }} data-help-page="store.tieOut.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Inventory vs Ledger</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }} data-help-section="store.tieOut.filter">
        <TextField
          data-help="store.tieOut.filter.asOf" label="Ledger as of" type="date" size="small" value={asOf}
          onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }}
        />
        <TextField
          data-help="store.tieOut.filter.from" label="Movements from" type="date" size="small" value={from}
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }}
        />
        <Button data-help="store.tieOut.run" variant="contained" onClick={load} disabled={loading}>Run</Button>
        <Button data-help="store.tieOut.csv" variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCsv} disabled={!report || loading}>
          Export CSV
        </Button>
      </Box>

      {loading ? <CircularProgress /> : report && (
        <>
          <CostingBasisNote method={report.lotCostingMethod} since={report.lotCostingSince} />
          {!report.inventoryAccountConfigured ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No inventory account is configured in Procurement Settings, so stock cannot be compared with the ledger.
            </Alert>
          ) : report.inBalance ? (
            <Alert severity="success" sx={{ mb: 2 }}>Stock value ties to the ledger ({report.inventoryAccountName}).</Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2 }}>
              Stock value differs from the ledger by {money(report.difference)}. Common causes: issues that were not posted
              (no consumption account configured), manual stock-ins, stock received before the ledger was in use, or journals
              posted by hand to {report.inventoryAccountName}.
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Stock valuation is the current value (stock keeps no value history); only the ledger side is as of {formatDate(report.asOf)}.
            {report.glAccountsUsed.length > 0 && <> Ledger accounts summed: {report.glAccountsUsed.join(', ')}.</>}
          </Alert>

          <TableContainer component={Paper} sx={{ mb: 3 }} data-help-section="store.tieOut.table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="store.tieOut.col.store">Store</TableCell>
                  <TableCell data-help="store.tieOut.col.stock" align="right">Stock valuation</TableCell>
                  <TableCell data-help="store.tieOut.col.gl" align="right">General ledger</TableCell>
                  <TableCell data-help="store.tieOut.col.diff" align="right">Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.stores.map(s => (
                  <TableRow key={s.storeId}>
                    <TableCell>{s.storeName}</TableCell>
                    <TableCell align="right">{money(s.stockValuation)}</TableCell>
                    <TableCell align="right" colSpan={2} sx={{ color: 'text.secondary' }}>
                      the ledger has no store dimension
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">{money(report.stockValuation)}</TableCell>
                  <TableCell align="right">{money(report.glBalance)}</TableCell>
                  <TableCell align="right" sx={{ color: report.difference !== 0 ? 'error.main' : 'inherit' }}>
                    {money(report.difference)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Stock movements with no journal since {formatDate(report.movementsFrom)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {report.unpostedIssueCount} unposted issue(s) worth at least {money(report.unpostedIssueValue)}
            {' '}and {report.manualStockInCount} unposted manual stock-in(s).
            {report.unvaluedMovementCount > 0 && ` ${report.unvaluedMovementCount} movement(s) have no recorded unit cost.`}
          </Typography>
          {report.movements.length === 0 ? (
            <Alert severity="success">Every stock issue in this period reached the ledger.</Alert>
          ) : (
            <TableContainer component={Paper} data-help-section="store.tieOut.unposted">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="store.tieOut.mv.date">Date</TableCell>
                    <TableCell data-help="store.tieOut.mv.store">Store</TableCell>
                    <TableCell data-help="store.tieOut.mv.item">Item</TableCell>
                    <TableCell data-help="store.tieOut.mv.kind">Kind</TableCell>
                    <TableCell data-help="store.tieOut.mv.qty" align="right">Qty</TableCell>
                    <TableCell data-help="store.tieOut.mv.value" align="right">Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.movements.map(m => (
                    <TableRow key={m.historyId}>
                      <TableCell>{formatDate(m.movedAt)}</TableCell>
                      <TableCell>{m.storeName}</TableCell>
                      <TableCell>{m.itemName}</TableCell>
                      <TableCell>{KIND_LABEL[m.kind] ?? m.kind}</TableCell>
                      <TableCell align="right">{m.quantity}</TableCell>
                      <TableCell align="right">{m.value == null ? '-' : money(m.value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {report.movementsTruncated && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Showing the latest {report.movements.length} movements.</Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default InventoryLedgerTieOut;
